import React, { useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Button,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  CATEGORY_ELIGIBILITIES,
  type CategoryEligibility,
  type CreateMediaAssetBody,
  type MediaAsset,
  type ReachMetric,
} from '~/services/MediaService';

import { marginPercent } from './formatting';

/**
 * The form's own shape.
 *
 * Numbers are held as strings because that is what an input gives back, and margin is a
 * percentage because that is how the rest of the UI reads it — both are converted on submit.
 */
export interface MediaAssetFormValues {
  name: string;
  description: string;
  category: string;
  category_eligibility: CategoryEligibility;
  hard_cost: string;
  rate: string;
  margin_percent: string;
  reach_metrics: Array<ReachMetric>;
}

export const blankAssetValues: MediaAssetFormValues = {
  name: '',
  description: '',
  category: '',
  category_eligibility: 'All categories',
  hard_cost: '0',
  rate: '0',
  margin_percent: '0',
  reach_metrics: [],
};

export const toFormValues = (asset: MediaAsset): MediaAssetFormValues => ({
  name: asset.name,
  description: asset.description,
  category: asset.category.name,
  category_eligibility: asset.category_eligibility.name as CategoryEligibility,
  hard_cost: String(asset.hard_cost),
  rate: String(asset.rate),
  // Rounded to four places because that is all the column holds.
  margin_percent: String(Math.round(asset.margin * 1000000) / 10000),
  reach_metrics: asset.reach_metrics.map((metric) => ({ ...metric })),
});

const toBody = (values: MediaAssetFormValues): CreateMediaAssetBody => ({
  name: values.name.trim(),
  description: values.description.trim(),
  category: values.category.trim(),
  category_eligibility: values.category_eligibility,
  hard_cost: Number(values.hard_cost),
  rate: Number(values.rate),
  margin: Number(values.margin_percent) / 100,
  reach_metrics: values.reach_metrics
    .filter((metric) => metric.key.trim())
    .map((metric) => ({ key: metric.key.trim(), value: Number(metric.value) })),
});

interface Props {
  /** Seeds the form, and re-seeds it whenever the reference changes. Memoise it. */
  values: MediaAssetFormValues;
  submitLabel: string;
  resetLabel: string;
  isSaving: boolean;
  onSubmit: (body: CreateMediaAssetBody) => void;
  /** Called whenever a field changes, so a page can clear its own success message. */
  onChange?: () => void;
}

/** The one asset form, shared by creating and editing so the two cannot drift apart. */
export default function MediaAssetForm({ values, submitLabel, resetLabel, isSaving, onSubmit, onChange }: Props) {
  const [form, setForm] = useState<MediaAssetFormValues>(values);

  useEffect(() => {
    setForm(values);
  }, [values]);

  const update = <K extends keyof MediaAssetFormValues>(key: K, value: MediaAssetFormValues[K]) => {
    onChange?.();
    setForm((current) => ({ ...current, [key]: value }));
  };

  const updateMetric = (index: number, metric: Partial<ReachMetric>) =>
    update(
      'reach_metrics',
      form.reach_metrics.map((current, position) => (position === index ? { ...current, ...metric } : current)),
    );

  const isComplete = Boolean(form.name.trim() && form.description.trim() && form.category.trim());

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={2}>
        <TextField
          label="Name"
          value={form.name}
          onChange={(event) => update('name', event.target.value)}
          required
          fullWidth
        />

        <TextField
          label="Description"
          value={form.description}
          onChange={(event) => update('description', event.target.value)}
          required
          fullWidth
          multiline
          minRows={3}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Category"
            value={form.category}
            onChange={(event) => update('category', event.target.value)}
            helperText="Free text. A category is created the first time an asset names it."
            required
            fullWidth
          />
          <TextField
            label="Eligibility"
            value={form.category_eligibility}
            onChange={(event) => update('category_eligibility', event.target.value as CategoryEligibility)}
            select
            fullWidth
          >
            {CATEGORY_ELIGIBILITIES.map((eligibility) => (
              <MenuItem key={eligibility} value={eligibility}>
                {eligibility}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Hard cost"
            type="number"
            value={form.hard_cost}
            onChange={(event) => update('hard_cost', event.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">A$</InputAdornment> }}
            fullWidth
          />
          <TextField
            label="Rate"
            type="number"
            value={form.rate}
            onChange={(event) => update('rate', event.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">A$</InputAdornment> }}
            fullWidth
          />
          <TextField
            label="Margin"
            type="number"
            value={form.margin_percent}
            onChange={(event) => update('margin_percent', event.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            helperText={`Rate and hard cost imply ${marginPercent(Number(form.rate), Number(form.hard_cost))}`}
            fullWidth
          />
        </Stack>

        <Divider />

        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1">Reach metrics</Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => update('reach_metrics', [...form.reach_metrics, { key: '', value: 0 }])}
          >
            Add metric
          </Button>
        </Stack>

        {form.reach_metrics.length === 0 && (
          <Typography color="text.secondary" variant="body2">
            None recorded.
          </Typography>
        )}

        {form.reach_metrics.map((metric, index) => (
          <Stack direction="row" spacing={2} alignItems="center" key={index}>
            <TextField
              label="Metric"
              value={metric.key}
              onChange={(event) => updateMetric(index, { key: event.target.value })}
              fullWidth
            />
            <TextField
              label="Value"
              type="number"
              value={metric.value}
              onChange={(event) => updateMetric(index, { value: Number(event.target.value) })}
              fullWidth
            />
            <IconButton
              aria-label={`Remove ${metric.key || 'metric'}`}
              onClick={() =>
                update(
                  'reach_metrics',
                  form.reach_metrics.filter((_, position) => position !== index),
                )
              }
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>
        ))}

        <Divider />

        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={() => onSubmit(toBody(form))} disabled={isSaving || !isComplete}>
            {isSaving ? 'Saving…' : submitLabel}
          </Button>
          <Button variant="text" onClick={() => setForm(values)} disabled={isSaving}>
            {resetLabel}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}
